// Phase 12CG-bis-UI (2026-05-31) — Row-level Lane Routing explainer.
//
// Consumes /api/v1/signal/missed-movers?date=YYYY-MM-DD&symbols=...
// and renders each item's `lane_routing_decision` dict (added Phase 12CG-bis)
// in plain English so an operator can answer:
//
//     "Why isn't SNOW on the Signal radar today?"
//
// without parsing raw EXCLUSION_* tokens.
//
// Display rules:
//   * If lane_routing_status === "ROUTED_TO_OTHER_LANE":
//       headline = "Routed to <Lane>"  (e.g. Large-Cap Signal Lane)
//   * If lane_routing_status === "TRUE_EXCLUSION":
//       headline = "True exclusion — <reason>"
//   * Else:
//       headline = sentence-case status fallback.
//
// Never renders raw JSON. Never renders snake_case keys. Never uses
// "excluded" for a routed item. Carries the canonical safety copy
// required by Phase 12CG-bis-UI.
//
// Marker strings (required by drift markers):
//   - "Lane Routing"
//   - "Lane routing does not change official Signal labels"
//   - "routed to another research lane rather than treated as a failed"
//   - "phase12cg-bis-ui"
import { useEffect, useState, useCallback } from "react";
import { Compass, RefreshCw, ShieldCheck, AlertTriangle, Activity, Sparkles } from "lucide-react";
import { apiV1Base } from "../../lib/apiConfig";
import { SectionExplainer, TermHelp } from "../BeginnerHelp";

const POLL_MS = 60_000;

// Default high-price / well-known names operators commonly ask about.
// These are the symbols most likely to trigger Large-Cap Lane routing
// (PRICE_ABOVE_MAX) when the upstream feed includes them.
const DEFAULT_SYMBOLS = "SNOW,NVDA,AAPL,MSFT,TSLA";

interface LaneRoutingDecision {
  assigned_lane?: string | null;
  lane_routing_status?: string | null;
  lane_routing_reason?: string | null;
  is_true_exclusion?: boolean | null;
  exclusion_display_category?: string | null;
  small_cap_lane_eligible?: boolean | null;
  large_cap_lane_eligible?: boolean | null;
  institutional_catalyst_lane_eligible?: boolean | null;
  // Phase 12CG-bis-IC (2026-05-31) — verified IC eligibility detail
  institutional_catalyst_reason?: string | null;
  institutional_catalyst_source?: string | null;
  institutional_catalyst_score?: number | null;
  institutional_catalyst_detected_at?: string | null;
  advisory_only?: boolean | null;
  does_not_place_orders?: boolean | null;
}

interface MissedMoverItem {
  symbol?: string;
  current_market_price?: number | null;
  current_day_change_pct?: number | null;
  exclusion_reason?: string | null;
  exclusion_stage?: string | null;
  lane_routing_decision?: LaneRoutingDecision | null;
  explanation_plain_english?: string | null;
  in_matrix_candidate_cache?: boolean | null;
  in_scoreboard_rows?: boolean | null;
}

interface MissedMoverPayload {
  status?: string;
  date?: string;
  symbols?: string[];
  movers_provider_state?: string;
  items?: MissedMoverItem[];
}

interface Envelope<T> { ok: boolean; data: T | null; error: { message: string; code: string } | null }

async function fetchMM(date: string, symbols: string): Promise<Envelope<MissedMoverPayload>> {
  try {
    const url = `${apiV1Base()}/signal/missed-movers?date=${encodeURIComponent(date)}&symbols=${encodeURIComponent(symbols)}`;
    const res = await fetch(url, { credentials: "include", headers: { accept: "application/json" }, cache: "no-store" });
    if (!res.ok) return { ok: false, data: null, error: { message: `HTTP ${res.status}`, code: String(res.status) } };
    return (await res.json()) as Envelope<MissedMoverPayload>;
  } catch (e) {
    return { ok: false, data: null, error: { message: String(e), code: "FETCH" } };
  }
}

function todayIsoDate(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
}

function laneIdToLabel(laneId: string | null | undefined): string {
  if (!laneId) return "—";
  if (laneId === "large_cap_signal_lane") return "Large-Cap Signal Lane";
  if (laneId === "sub_dollar_research_lane") return "Sub-Dollar Research Watch";
  // Fallback: title-case any unknown lane id without leaking snake_case.
  return laneId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function reasonToPlainEnglish(reason: string | null | undefined): string {
  if (!reason) return "—";
  const map: Record<string, string> = {
    price_above_small_cap_band:   "Price above the small-cap band",
    price_below_small_cap_floor:  "Price below the small-cap floor",
    liquidity_insufficient:       "Insufficient liquidity",
    spread_too_wide:              "Spread too wide",
    tradability_failed:           "Tradability check failed",
    missing_price_data:           "Missing price data",
    not_on_movers_radar:          "Not on today's market-wide movers radar",
    not_top_ranked:               "Did not make today's top-ranked candidates",
    unclassified:                 "Unclassified reason",
  };
  return map[reason] || reason.replace(/_/g, " ");
}

function statusBadge(decision: LaneRoutingDecision | null | undefined): { label: string; tone: string } {
  if (!decision) return { label: "Unknown", tone: "neutral" };
  switch (decision.lane_routing_status) {
    case "ROUTED_TO_OTHER_LANE":
      return { label: "Routed", tone: "emerald" };
    case "TRUE_EXCLUSION":
      return { label: "True exclusion", tone: "amber" };
    case "PRIMARY_LANE_CANDIDATE":
      return { label: "Primary lane", tone: "blue" };
    case "UNCLASSIFIED":
    default:
      return { label: "Unclassified", tone: "neutral" };
  }
}

function toneClasses(tone: string): string {
  switch (tone) {
    case "emerald": return "border-emerald-500/30 bg-emerald-500/5 text-emerald-400";
    case "amber":   return "border-amber-500/30 bg-amber-500/5 text-amber-400";
    case "blue":    return "border-sky-500/30 bg-sky-500/5 text-sky-400";
    default:        return "border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--muted-text)]";
  }
}

function HeadlineForItem({ item }: { item: MissedMoverItem }) {
  const dec = item.lane_routing_decision;
  if (!dec) {
    return <span className="text-sm text-[var(--app-text)]">No lane decision available.</span>;
  }
  if (dec.lane_routing_status === "ROUTED_TO_OTHER_LANE") {
    return (
      <span className="text-sm text-[var(--app-text)]">
        Routed to <strong>{laneIdToLabel(dec.assigned_lane)}</strong>
        <span className="text-[var(--muted-text)]"> · {reasonToPlainEnglish(dec.lane_routing_reason)}</span>
      </span>
    );
  }
  if (dec.lane_routing_status === "TRUE_EXCLUSION") {
    return (
      <span className="text-sm text-[var(--app-text)]">
        <strong>True exclusion</strong>
        <span className="text-[var(--muted-text)]"> — {reasonToPlainEnglish(dec.lane_routing_reason)}</span>
      </span>
    );
  }
  if (dec.lane_routing_status === "PRIMARY_LANE_CANDIDATE") {
    return <span className="text-sm text-[var(--app-text)]">On The Signal radar (primary lane candidate).</span>;
  }
  return (
    <span className="text-sm text-[var(--app-text)]">
      Status: <strong>{(dec.lane_routing_status || "UNKNOWN").replace(/_/g, " ").toLowerCase()}</strong>
    </span>
  );
}

function EligibilityChips({ dec }: { dec: LaneRoutingDecision }) {
  const chips: { label: string; on: boolean }[] = [
    { label: "Small-Cap lane",            on: !!dec.small_cap_lane_eligible },
    { label: "Large-Cap lane",            on: !!dec.large_cap_lane_eligible },
    { label: "Institutional Catalyst",    on: !!dec.institutional_catalyst_lane_eligible },
  ];
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map(c => (
        <span
          key={c.label}
          className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${c.on
            ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
            : "border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--muted-text)]"}`}
        >
          {c.label}: {c.on ? "eligible" : "not eligible"}
        </span>
      ))}
    </div>
  );
}

// Phase 12CG-bis-IC — Institutional Catalyst detail row.
// Renders distinct copy for the three honest states:
//   eligible=true                                -> green chip + verified reason + score + detected timestamp
//   eligible=false, source=institutional_catalyst_engine -> muted "no verified signal" + freshness
//   source=UNAVAILABLE                            -> amber "catalyst source unavailable"
function InstitutionalCatalystDetail({ dec }: { dec: LaneRoutingDecision }) {
  const eligible = !!dec.institutional_catalyst_lane_eligible;
  const source = dec.institutional_catalyst_source || null;
  const reason = dec.institutional_catalyst_reason || null;
  const score = typeof dec.institutional_catalyst_score === "number" ? dec.institutional_catalyst_score : null;
  const detected = dec.institutional_catalyst_detected_at || null;

  function fmtDetected(): string {
    if (!detected) return "";
    try {
      const d = new Date(detected);
      if (isNaN(d.getTime())) return "";
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York", year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      }).format(d) + " ET";
    } catch { return ""; }
  }
  const detectedEt = fmtDetected();

  if (source === "UNAVAILABLE") {
    return (
      <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/5 text-amber-400 px-2 py-1.5 text-[11px]" data-testid="lane-routing-ic-unavailable">
        <strong className="uppercase tracking-widest">Institutional Catalyst:</strong>{" "}
        Catalyst source unavailable. Lane eligibility cannot be confirmed.
      </div>
    );
  }
  if (eligible) {
    return (
      <div className="mt-2 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-2 py-1.5 text-[11px]" data-testid="lane-routing-ic-eligible">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span><strong className="uppercase tracking-widest">Institutional Catalyst Eligible</strong></span>
          {score !== null && (
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10">
              Score {score}
            </span>
          )}
        </div>
        {reason && <div className="mt-1 text-[var(--app-text)]">{reason}</div>}
        {detectedEt && <div className="mt-1 text-[10px] text-[var(--muted-text)]">Detected {detectedEt}</div>}
      </div>
    );
  }
  // eligible=false, source=institutional_catalyst_engine
  return (
    <div className="mt-2 rounded border border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--muted-text)] px-2 py-1.5 text-[11px]" data-testid="lane-routing-ic-not-eligible">
      <strong className="uppercase tracking-widest">Institutional Catalyst:</strong>{" "}
      No verified institutional catalyst signal.
      {detectedEt && <span className="ml-1 text-[10px]">(Cache as of {detectedEt})</span>}
    </div>
  );
}

function ItemRow({ item }: { item: MissedMoverItem }) {
  const dec = item.lane_routing_decision || null;
  const badge = statusBadge(dec);
  const price = typeof item.current_market_price === "number" ? `$${item.current_market_price.toFixed(2)}` : "—";
  const chg = typeof item.current_day_change_pct === "number" ? `${item.current_day_change_pct >= 0 ? "+" : ""}${item.current_day_change_pct.toFixed(2)}%` : "—";
  return (
    <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3" data-testid="lane-routing-item">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="font-mono font-black text-sm text-[var(--app-text)]">{item.symbol || "—"}</div>
          <div className="text-xs text-[var(--muted-text)]">{price} · {chg}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${toneClasses(badge.tone)}`}>
          {badge.label}
        </span>
      </div>
      <div className="mt-2">
        <HeadlineForItem item={item} />
      </div>
      {dec && <EligibilityChips dec={dec} />}
      {dec && <InstitutionalCatalystDetail dec={dec} />}
      {item.explanation_plain_english && (
        <div className="mt-2 text-[11px] text-[var(--muted-text)]" data-testid="lane-routing-explanation">
          {item.explanation_plain_english}
        </div>
      )}
    </div>
  );
}

// Phase 12CG-bis-DL (2026-05-31) - read prefill from URL.
// Supports both ?laneSymbol=NVDA and ?laneSymbols=NVDA,TSM. Pure read of
// window.location.search; never writes back. Returns null when no usable
// prefill is present so the default DEFAULT_SYMBOLS list still wins.
function readLaneSymbolPrefill(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const usp = new URLSearchParams(window.location.search);
    const raw = usp.get("laneSymbols") || usp.get("laneSymbol") || "";
    if (!raw) return null;
    const cleaned = raw.split(",")
      .map((s) => s.trim().toUpperCase())
      .filter((s) => /^[A-Z][A-Z0-9.\-]{0,9}$/.test(s))
      .slice(0, 10)
      .join(",");
    return cleaned || null;
  } catch { return null; }
}

// Phase 12CG-bis-SP (2026-05-31) - same-page handoff from sibling Trading-UI
// surfaces (e.g. InstitutionalCatalystWatch). Pure validator + no I/O.
function normalizeLaneSymbolPayload(raw: unknown): string | null {
  let s = "";
  if (typeof raw === "string") s = raw;
  else if (raw && typeof raw === "object") {
    const r = raw as { symbol?: unknown; symbols?: unknown };
    if (typeof r.symbols === "string") s = r.symbols;
    else if (typeof r.symbol === "string") s = r.symbol;
  }
  if (!s) return null;
  const cleaned = s.split(",")
    .map((t) => t.trim().toUpperCase())
    .filter((t) => /^[A-Z][A-Z0-9.\-]{0,9}$/.test(t))
    .slice(0, 10)
    .join(",");
  return cleaned || null;
}
const LANE_ROUTING_SET_EVENT = "adversyn:set-lane-symbol";

export default function LaneRoutingExplainer() {
  const [date, setDate] = useState<string>(todayIsoDate());
  // Phase 12CG-bis-DL - allow URL prefill (?laneSymbol or ?laneSymbols)
  const _initialPrefill = readLaneSymbolPrefill();
  const _initial = _initialPrefill || DEFAULT_SYMBOLS;
  const [symbolsInput, setSymbolsInput] = useState<string>(_initial);
  const [activeSymbols, setActiveSymbols] = useState<string>(_initial);
  const [payload, setPayload] = useState<MissedMoverPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const env = await fetchMM(date, activeSymbols);
    if (env.ok && env.data) {
      setPayload(env.data);
    } else if (env.error) {
      setError(env.error.message);
    }
    setLoading(false);
  }, [date, activeSymbols]);

  // Phase 12CG-bis-SP - subscribe to same-page Lane Routing requests
  useEffect(() => {
    function onSet(ev: Event) {
      const ce = ev as CustomEvent<unknown>;
      const cleaned = normalizeLaneSymbolPayload(ce.detail);
      if (cleaned) {
        setSymbolsInput(cleaned);
        setActiveSymbols(cleaned);
      }
    }
    window.addEventListener(LANE_ROUTING_SET_EVENT, onSet as EventListener);
    return () => window.removeEventListener(LANE_ROUTING_SET_EVENT, onSet as EventListener);
  }, []);

  // Hotfix 12CG-bis-HF (2026-05-31) - when arriving from a deep link that
  // includes ?laneSymbol(s)= or #lane-routing, scroll the Lane Routing
  // card into view so the operator doesn't land at the top of /signal.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let wantsScroll = false;
    try {
      const hash = (window.location.hash || "").toLowerCase();
      if (hash === "#lane-routing" || hash.startsWith("#lane-routing")) wantsScroll = true;
      if (!wantsScroll) {
        const usp = new URLSearchParams(window.location.search);
        if (usp.get("laneSymbol") || usp.get("laneSymbols")) wantsScroll = true;
      }
    } catch { /* no-op */ }
    if (!wantsScroll) return;
    // Defer to next frame so the root <div id="lane-routing"> is mounted.
    const raf = window.requestAnimationFrame(() => {
      try {
        const el = document.getElementById("lane-routing");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } catch { /* no-op */ }
    });
    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  function onApply(e: React.FormEvent) {
    e.preventDefault();
    const cleaned = symbolsInput.split(",").map(s => s.trim().toUpperCase()).filter(Boolean).slice(0, 10).join(",");
    if (cleaned) setActiveSymbols(cleaned);
  }

  const items = payload?.items || [];
  const providerState = payload?.movers_provider_state || "—";

  return (
    <div
      id="lane-routing"
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4 shadow-sm scroll-mt-20"
      data-testid="lane-routing-explainer"
    >
      {/* phase12cg-bis-ui marker */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg text-emerald-500">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--muted-text)]">The Signal · Educational</div>
            <h2 className="text-lg font-black uppercase italic text-[var(--app-text)] tracking-tight">Lane Routing</h2>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--muted-text)]">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Advisory only · no order action</span>
          <button onClick={load} className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--app-text)] hover:text-emerald-500" data-testid="lane-routing-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <SectionExplainer
        testId="beginner-help-lane-routing"
        title="Lane Routing beginner guide"
        what={<>Lane Routing is sorting a stock into the right research bucket before judging it inside The Signal.</>}
        why={<>It prevents a stock from looking like a failed Signal candidate when it really belongs in a different research lane.</>}
        lookAt={<>Look at the routed badge, the assigned lane, and the plain-English reason. For larger companies, this often explains why they are reviewed outside the small-cap lane.</>}
        notDo={<>It does not change official Signal labels, the platform&apos;s original result tag for the signal. It also does not change hit-rate logic or place orders.</>}
      >
        <TermHelp term="Lane Routing" />
        <TermHelp term="Official Signal labels" />
        <TermHelp term="Candidate" />
        <TermHelp term="Candidate Promotion" />
      </SectionExplainer>

      <form className="mb-3 flex items-center gap-2 flex-wrap" onSubmit={onApply}>
        <label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Symbols</label>
        <input
          type="text"
          value={symbolsInput}
          onChange={(e) => setSymbolsInput(e.target.value)}
          maxLength={120}
          placeholder="SNOW,NVDA,AAPL"
          className="flex-1 min-w-[200px] px-2 py-1 rounded border border-[var(--card-border)] bg-[var(--input-bg)] text-[12px] font-mono text-[var(--app-text)] focus:outline-none focus:border-emerald-500/50"
          data-testid="lane-routing-input"
        />
        <label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-2 py-1 rounded border border-[var(--card-border)] bg-[var(--input-bg)] text-[12px] text-[var(--app-text)] focus:outline-none focus:border-emerald-500/50"
          data-testid="lane-routing-date"
        />
        <button type="submit" className="px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-[10px] uppercase tracking-widest hover:bg-emerald-500/20">
          Apply
        </button>
      </form>

      <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">
        <Sparkles className="inline w-3 h-3 mr-1" />
        Provider state: <strong className="text-[var(--app-text)]">{providerState}</strong> · Date: {payload?.date || date}
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 text-sm text-[var(--muted-text)]" data-testid="lane-routing-empty">
          <Activity className="inline w-4 h-4 mr-2" />
          {loading ? "Loading lane routing decisions…" : "No items returned for this date / symbol set."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => <ItemRow key={(it.symbol || "") + i} item={it} />)}
        </div>
      )}

      <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-3 py-2 text-[11px]" data-testid="lane-routing-safety">
        <strong className="uppercase tracking-widest">Lane routing</strong> means sorting a stock into the right research bucket. It does not change official Signal labels, the platform&apos;s original result tag for the signal, or the hit rate.
        Symbols above are routed to another research lane rather than treated as a failed small-cap candidate when the
        backend says so. True exclusions remain liquidity, spread, missing data, stale provider data, halted stocks,
        stocks that cannot be reviewed, or failed risk validation.
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 text-rose-400 px-3 py-2 text-[11px]" data-testid="lane-routing-error">
          <AlertTriangle className="inline w-3 h-3 mr-1.5" /> {error}
        </div>
      )}
    </div>
  );
}
